import { useRequiredContext } from "@redotech/react-util/context";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { Message } from "@redotech/redo-model/agents/llm-messages";
import {
  AiChatProvider,
  AiChatStreamEvent,
} from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import {
  extractAttachmentUrls,
  extractTextFromContent,
  transformMessagesForBackend,
} from "@redotech/redo-web/ai/chat/utils/messages";
import { UploaderContext } from "@redotech/redo-web/image-upload";
import * as React from "react";
import {
  memo,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { EscalateToHumanTool } from "../../help-chat/escalate-to-human-tool";
import {
  EscalationProvider,
  useEscalationContext,
} from "../../help-chat/escalation-context";
import { USE_IN_CHAT_ESCALATION } from "../../help-chat/feature-flags";
import { HelpChatGlobal } from "../../help-chat/help-chat-global";
import { CreateHyperLinkTool } from "./global-search-tools/create-hyper-link-tool";
import { UpdateIntegrationTool } from "./global-search-tools/update-integration-tool";

interface GlobalAIProps {
  children?: React.ReactNode;
}

export const GlobalAI = memo(function GlobalAI({ children }: GlobalAIProps) {
  // Always wrap in EscalationProvider (needed for HelpChatGlobal hook)
  // Only wrap in ClientUploader when escalation feature is enabled
  if (USE_IN_CHAT_ESCALATION) {
    return (
      <EscalationProvider>
        <ClientUploader>
          <GlobalAIContentWithEscalation>
            {children}
          </GlobalAIContentWithEscalation>
        </ClientUploader>
      </EscalationProvider>
    );
  }

  // Legacy behavior: wrap in EscalationProvider but don't use escalation features
  return (
    <EscalationProvider>
      <GlobalAIContentLegacy>{children}</GlobalAIContentLegacy>
    </EscalationProvider>
  );
});

/**
 * Legacy content component - matches main branch behavior (Intercom popup)
 */
const GlobalAIContentLegacy = memo(function GlobalAIContentLegacy({
  children,
}: GlobalAIProps) {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const user = useRequiredContext(UserContext);
  const { pageContext } = React.useContext(GlobalAgentContext);

  // state is used to control the UI thread context while refs are used during sends
  const [_currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [_triageThreadId, setTriageThreadId] = useState<string | undefined>();
  const triageThreadCallbackRef = useRef<((threadId: string) => void) | null>(
    null,
  );

  // Use refs to always have the most current values for the callback
  const currentThreadIdRef = useRef<string | undefined>();
  const triageThreadIdRef = useRef<string | undefined>();

  const toolComponents = useMemo(
    () => ({
      "update-integration": UpdateIntegrationTool,
      "create-hyper-link": CreateHyperLinkTool,
      "escalate-to-human": EscalateToHumanTool,
    }),
    [],
  );

  const handleToolResult = useCallback(async (args: any) => {
    if (args.toolName === "escalate-to-human") {
      // No command palette to close in AI-only mode
    }
  }, []);

  const handleChatOpen = useCallback(() => {
    setCurrentThreadId(undefined);
    setTriageThreadId(undefined);
    currentThreadIdRef.current = undefined;
    triageThreadIdRef.current = undefined;
  }, []);

  const handleLoadThread = useCallback((threadId: string, _thread: unknown) => {
    // When loading from thread history, always continue with the triage agent
    // The triage agent will handle routing to child agents as needed
    // Continue with triage agent
    setCurrentThreadId(threadId);
    setTriageThreadId(threadId);
    currentThreadIdRef.current = threadId;
    triageThreadIdRef.current = threadId;
  }, []);

  const streamMessageToAI = useCallback(
    async function* ({
      newMessages,
      signal,
    }: {
      newMessages: Message[];
      signal: AbortSignal;
    }): AsyncGenerator<AiChatStreamEvent, void, undefined> {
      const transformedMessages = transformMessagesForBackend(newMessages);

      // Use refs to get the most current values
      const currentCurrentThreadId = currentThreadIdRef.current;
      const currentTriageThreadId = triageThreadIdRef.current;

      // Page context: if set (e.g., from Returns Ask AI), always include it
      // so the triage agent has consistent context on this page.

      const stream = merchantRpcClient.streamMerchantSupportAgent(
        {
          messages: transformedMessages,
          currentThreadId: currentCurrentThreadId,
          triageThreadId: currentTriageThreadId,
          userEmail: user.email,
          // Always pass pageContext when available (not only for new threads)
          ...(pageContext ? { pageContext } : {}),
        },
        { signal },
      );

      for await (const event of stream) {
        switch (event.type) {
          case "text-delta":
            yield { type: "text-delta", textDelta: event.textDelta };
            break;
          case "tool-call":
            yield { type: "tool-call", toolCall: event.toolCall };
            break;
          case "reasoning":
            yield { type: "reasoning", text: event.text };
            break;
          case "status":
            yield { type: "status", status: event.status };
            break;
          case "complete": {
            const { thread } = event;

            // Check if this created a new triage thread
            const wasNewTriageThread =
              !currentTriageThreadId && thread.triageThreadId;

            // Update both state and refs
            setCurrentThreadId(thread.currentThreadId.toString());
            setTriageThreadId(thread.triageThreadId.toString());
            currentThreadIdRef.current = thread.currentThreadId.toString();
            triageThreadIdRef.current = thread.triageThreadId.toString();

            // If a new triage thread was created, trigger thread refresh
            if (wasNewTriageThread) {
              triageThreadCallbackRef.current?.(
                thread.triageThreadId.toString(),
              );
            }

            yield { type: "complete", messages: thread.newMessages };
            break;
          }
          case "error":
            yield { type: "error", error: event.error };
            break;
        }
      }
    },
    [merchantRpcClient, user.email, pageContext],
  );

  React.useEffect(() => {
    return () => {
      triageThreadCallbackRef.current = null;
    };
  }, []);

  return (
    <AiChatProvider
      additionalToolComponents={toolComponents}
      onToolResult={handleToolResult}
      streamMessageToAI={streamMessageToAI}
    >
      {children}
      <HelpChatGlobal
        onChatOpen={handleChatOpen}
        onLoadThread={(threadId: string, thread: unknown) =>
          handleLoadThread(threadId, thread)
        }
        onRegisterTriageThreadCallback={(
          callback: (threadId: string) => void,
        ) => {
          triageThreadCallbackRef.current = callback;
        }}
      />
    </AiChatProvider>
  );
});

/**
 * New content component with in-chat escalation support
 */
const GlobalAIContentWithEscalation = memo(
  function GlobalAIContentWithEscalation({ children }: GlobalAIProps) {
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const user = useRequiredContext(UserContext);
    const uploader = useContext(UploaderContext);
    const { pageContext } = React.useContext(GlobalAgentContext);
    const { escalation, setTriageThreadId: setEscalationTriageThreadId } =
      useEscalationContext();

    // state is used to control the UI thread context while refs are used during sends
    const [_currentThreadId, setCurrentThreadId] = useState<
      string | undefined
    >();
    const [_triageThreadId, setTriageThreadId] = useState<string | undefined>();
    const triageThreadCallbackRef = useRef<((threadId: string) => void) | null>(
      null,
    );

    // Use refs to always have the most current values for the callback
    const currentThreadIdRef = useRef<string | undefined>();
    const triageThreadIdRef = useRef<string | undefined>();

    const toolComponents = useMemo(
      () => ({
        "update-integration": UpdateIntegrationTool,
        "create-hyper-link": CreateHyperLinkTool,
        "escalate-to-human": EscalateToHumanTool,
      }),
      [],
    );

    const handleToolResult = useCallback(async (args: any) => {
      if (args.toolName === "escalate-to-human") {
        // No command palette to close in AI-only mode
      }
    }, []);

    const handleChatOpen = useCallback(() => {
      setCurrentThreadId(undefined);
      setTriageThreadId(undefined);
      currentThreadIdRef.current = undefined;
      triageThreadIdRef.current = undefined;
      // Also clear escalation triageThreadId when starting new chat
      setEscalationTriageThreadId(null);
    }, [setEscalationTriageThreadId]);

    const handleLoadThread = useCallback(
      (threadId: string, _thread: unknown) => {
        // When loading from thread history, always continue with the triage agent
        // The triage agent will handle routing to child agents as needed
        // Continue with triage agent
        setCurrentThreadId(threadId);
        setTriageThreadId(threadId);
        currentThreadIdRef.current = threadId;
        triageThreadIdRef.current = threadId;
        // Update escalation context with the triage thread ID
        setEscalationTriageThreadId(threadId);
      },
      [setEscalationTriageThreadId],
    );

    const streamMessageToAI = useCallback(
      async function* ({
        newMessages,
        signal,
      }: {
        newMessages: Message[];
        signal: AbortSignal;
      }): AsyncGenerator<AiChatStreamEvent, void, undefined> {
        // If escalation is active with human support, route messages to Intercom instead of AI
        if (
          escalation.status === "human-active" &&
          escalation.escalationId &&
          newMessages.length > 0
        ) {
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.role === "user") {
            // Extract text content (filtering out hidden image-data and video-data metadata)
            const messageContent = extractTextFromContent(lastMessage.content);

            // Extract image and video URLs from the message content
            const attachmentUrls = Array.isArray(lastMessage.content)
              ? extractAttachmentUrls(lastMessage.content)
              : [];

            // Send message if there's text or attachments
            if (messageContent || attachmentUrls.length > 0) {
              const result = await merchantRpcClient.sendEscalationMessage({
                escalationId: escalation.escalationId,
                message: messageContent || "",
                ...(attachmentUrls.length > 0 && { attachmentUrls }),
              });

              if (!result.success) {
                yield {
                  type: "error",
                  error: result.error ?? "Failed to send message",
                };
                return;
              }

              // Return empty messages since the AI doesn't respond during human support
              yield { type: "complete", messages: [] };
              return;
            }
          }
        }

        const transformedMessages = transformMessagesForBackend(newMessages);

        // Use refs to get the most current values
        const currentCurrentThreadId = currentThreadIdRef.current;
        const currentTriageThreadId = triageThreadIdRef.current;

        // Page context: if set (e.g., from Returns Ask AI), always include it
        // so the triage agent has consistent context on this page.

        const stream = merchantRpcClient.streamMerchantSupportAgent(
          {
            messages: transformedMessages,
            currentThreadId: currentCurrentThreadId,
            triageThreadId: currentTriageThreadId,
            userEmail: user.email,
            // Always pass pageContext when available (not only for new threads)
            ...(pageContext ? { pageContext } : {}),
          },
          { signal },
        );

        for await (const event of stream) {
          switch (event.type) {
            case "text-delta":
              yield { type: "text-delta", textDelta: event.textDelta };
              break;
            case "tool-call":
              yield { type: "tool-call", toolCall: event.toolCall };
              break;
            case "reasoning":
              yield { type: "reasoning", text: event.text };
              break;
            case "status":
              yield { type: "status", status: event.status };
              break;
            case "complete": {
              const { thread } = event;

              // Check if this created a new triage thread
              const wasNewTriageThread =
                !currentTriageThreadId && thread.triageThreadId;

              // Update both state and refs
              setCurrentThreadId(thread.currentThreadId.toString());
              setTriageThreadId(thread.triageThreadId.toString());
              currentThreadIdRef.current = thread.currentThreadId.toString();
              triageThreadIdRef.current = thread.triageThreadId.toString();

              // If a new triage thread was created, trigger thread refresh and update escalation context
              if (wasNewTriageThread) {
                triageThreadCallbackRef.current?.(
                  thread.triageThreadId.toString(),
                );
                setEscalationTriageThreadId(thread.triageThreadId.toString());
              }

              yield { type: "complete", messages: thread.newMessages };
              break;
            }
            case "error":
              yield { type: "error", error: event.error };
              break;
          }
        }
      },
      [
        merchantRpcClient,
        user.email,
        pageContext,
        escalation.status,
        escalation.escalationId,
        setEscalationTriageThreadId,
      ],
    );

    React.useEffect(() => {
      return () => {
        triageThreadCallbackRef.current = null;
      };
    }, []);

    // Only enable file uploads when connected to human support
    const humanSupportUploader =
      escalation.status === "human-active" ? uploader : undefined;

    // File upload options for human support mode - allow images and videos
    const humanSupportFileOptions = useMemo(
      () => ({
        inputAccept: "image/*,video/*",
        inputMultiple: true,
        inputLabel: "Upload from computer",
      }),
      [],
    );

    return (
      <AiChatProvider
        additionalToolComponents={toolComponents}
        fileUploadOptions={humanSupportFileOptions}
        isHumanSupportMode={escalation.status === "human-active"}
        onToolResult={handleToolResult}
        streamMessageToAI={streamMessageToAI}
        uploader={humanSupportUploader}
      >
        {children}
        <HelpChatGlobal
          onChatOpen={handleChatOpen}
          onLoadThread={(threadId: string, thread: unknown) =>
            handleLoadThread(threadId, thread)
          }
          onRegisterTriageThreadCallback={(
            callback: (threadId: string) => void,
          ) => {
            triageThreadCallbackRef.current = callback;
          }}
        />
      </AiChatProvider>
    );
  },
);
